import {
  INotification,
  notificationEnums,
} from "@freetech/models/notifications";

const truncateText = (text: string, maxLength: number = 200): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

export const getFreeloNotificationDescription = (
  notification: INotification
): string | undefined => {
  const { type, afterData, beforeData } = notification;

  switch (type) {
    case notificationEnums.FreeloNotificationType.FREELO_BOARD_CREATED:
      if (afterData && "description" in afterData) {
        return truncateText(afterData.description as string);
      }
      return "New Freelo board is available";

    case notificationEnums.FreeloNotificationType.FREELO_BOARD_UPDATED:
      if (afterData && beforeData) {
        const changes = [];
        if (
          "name" in afterData &&
          "name" in beforeData &&
          afterData.name !== beforeData.name
        ) {
          changes.push(`Name: "${beforeData.name}" → "${afterData.name}"`);
        }
        if (
          "description" in afterData &&
          "description" in beforeData &&
          afterData.description !== beforeData.description
        ) {
          const oldDesc = truncateText(beforeData.description as string, 100);
          const newDesc = truncateText(afterData.description as string, 100);
          changes.push(`Description: "${oldDesc}" → "${newDesc}"`);
        }
        if (changes.length > 0) {
          return changes.join(" | ");
        }
      }
      return "Board details have been updated";

    case notificationEnums.FreeloNotificationType.FREELO_BOARD_DELETED:
      if (beforeData && "description" in beforeData) {
        return `Deleted board: ${truncateText(beforeData.description as string)}`;
      }
      return "Board has been removed from the system";
    
    case notificationEnums.FreeloNotificationType.FREELO_CARD_CREATED:
      if (afterData && "description" in afterData) {
        return truncateText(afterData.description as string);
      }
      return "New card added to the board";

    case notificationEnums.FreeloNotificationType.FREELO_CARD_UPDATED:
      if (afterData && beforeData) {
        const changes = [];
        if (
          "title" in afterData &&
          "title" in beforeData &&
          afterData.title !== beforeData.title
        ) {
          changes.push(`Title: "${beforeData.title}" → "${afterData.title}"`);
        }
        if (
          "description" in afterData &&
          "description" in beforeData &&
          afterData.description !== beforeData.description
        ) {
          const oldDesc = (beforeData.description as string) || "(empty)";
          const newDesc = (afterData.description as string) || "(empty)";
          
          if (oldDesc === "(empty)") {
            changes.push(`Description added: "${truncateText(newDesc)}"`);
          } else if (newDesc === "(empty)") {
            changes.push(`Description removed (was: "${truncateText(oldDesc)}")`);
          } else {
            changes.push(`Description: "${truncateText(oldDesc, 100)}" → "${truncateText(newDesc, 100)}"`);
          }
        }
        if (
          "dueDate" in afterData &&
          "dueDate" in beforeData &&
          afterData.dueDate !== beforeData.dueDate
        ) {
          const oldDue = beforeData.dueDate ? new Date(beforeData.dueDate as string).toLocaleDateString() : "none";
          const newDue = afterData.dueDate ? new Date(afterData.dueDate as string).toLocaleDateString() : "none";
          changes.push(`Due date: ${oldDue} → ${newDue}`);
        }
        if (
          "status" in afterData &&
          "status" in beforeData &&
          afterData.status !== beforeData.status
        ) {
          changes.push(`Status: ${beforeData.status} → ${afterData.status}`);
        }
        if (changes.length > 0) {
          return changes.join(" | ");
        }
      }
      return "Card details have been updated";

    case notificationEnums.FreeloNotificationType.FREELO_CARD_DELETED:
      if (beforeData && "title" in beforeData) {
        return `Deleted card: "${beforeData.title}"${beforeData.description ? ` - ${truncateText(beforeData.description as string, 100)}` : ""}`;
      }
      return "Card has been removed from the board";

    case notificationEnums.FreeloNotificationType.FREELO_CARD_ASSIGNED:
      if (afterData && "assignedBy" in afterData) {
        const details = [`Assigned by ${afterData.assignedBy}`];
        if ("dueDate" in afterData && afterData.dueDate) {
          const dueDate = new Date(afterData.dueDate as string);
          details.push(`Due: ${dueDate.toLocaleDateString()}`);
        }
        if ("title" in afterData) {
          details.push(`"${afterData.title}"`);
        }
        return details.join(" - ");
      }
      return "You have been assigned to this card";

    case notificationEnums.FreeloNotificationType.FREELO_CARD_COMMENT_MENTION:
      if (afterData && "commentText" in afterData) {
        const comment = afterData.commentText as string;
        return `Mentioned in comment: "${truncateText(comment, 150)}"`;
      }
      return "You were mentioned in a comment";

    case notificationEnums.FreeloNotificationType.FREELO_CARD_STATUS_CHANGED:
      if (afterData && beforeData) {
        const details = [];
        if ("status" in afterData && "status" in beforeData) {
          details.push(`${beforeData.status} → ${afterData.status}`);
        }
        if ("updatedBy" in afterData) {
          details.push(`by ${afterData.updatedBy}`);
        }
        if (details.length > 0) {
          return `Status changed: ${details.join(" ")}`;
        }
      }
      return "Card status has been updated";

    case notificationEnums.FreeloNotificationType.FREELO_DEADLINE_APPROACHING:
      if (afterData) {
        const details = [];
        if ("title" in afterData) {
          details.push(`"${afterData.title}"`);
        }
        if ("hoursRemaining" in afterData) {
          details.push(`Due in ${afterData.hoursRemaining} hours`);
        } else if ("dueDate" in afterData) {
          const dueDate = new Date(afterData.dueDate as string);
          details.push(`Due: ${dueDate.toLocaleDateString()}`);
        }
        if (details.length > 0) {
          return `Deadline approaching: ${details.join(" - ")}`;
        }
      }
      return "Card deadline is approaching";

    default:
      return undefined;
  }
};
